import {useQueries} from "react-query"
import moment from "moment"
import useAuth from "auth/useAuth"
import {areaDataQuery, getCachedAreaData} from "hooks/areaDataQuery"


function getShiftDates(startSaturday) {
    let start
    if (startSaturday) {
        start = moment(startSaturday)
    } else {
        const now = moment()
        start = moment(now).day(6) // Saturday of current week
        if (start.isAfter(now)) start.subtract(7, 'days')
        if (start.isSame(now, 'day') && now.hour() < 18) start.subtract(7, 'days')
    }

    let end = moment(start).add(7, 'days')

    const dates = []
    for (let d = moment(start); d.isSameOrBefore(end, 'day'); d.add(1, 'day')) {
        dates.push(d.format('YYYY-MM-DD'))
    }
    return {start, end, dates}
}


export default function useShiftRescues(area, startSaturday) {

    const {auth, API} = useAuth()
    const {start, end, dates} = getShiftDates(startSaturday)

    const queries = useQueries(
        dates.map(date => ({
            ...areaDataQuery(auth, API, area, date),
            staleTime: 2 * 60 * 1000,
            refetchInterval: 3 * 60 * 1000,
            retry: 1,
            enabled: !!area,
            placeholderData: () => getCachedAreaData(area, date)
        }))
    )

    const isLoading = queries.some(q => q.isLoading)
    const isFetching = queries.some(q => q.isFetching)
    const refetch = () => queries.forEach(q => q.refetch())

    if (isLoading || !area) {
        return {rescues: [], isLoading, isFetching, refetch, ownerCounts: new Map(), participantCounts: new Map(), start, end}
    }

    const startDate = start.format('YYYY-MM-DD')
    const endDate = end.format('YYYY-MM-DD')
    const seen = new Set()
    const rescues = []

    for (let i = 0; i < dates.length; i++) {
        const date = dates[i]
        const data = queries[i].data
        if (!data?.rescues) continue

        for (const rescue of data.rescues) {
            if (seen.has(rescue.id)) continue

            const firstEventTime = rescue.events?.[0]?.time ?? rescue.time ?? `${rescue.date}T12:00:00`
            if (!firstEventTime) continue

            // Filter boundary Saturdays
            if (date === startDate && firstEventTime < `${startDate}T18:00:00`) continue
            if (date === endDate && firstEventTime >= `${endDate}T18:00:00`) continue

            seen.add(rescue.id)
            rescues.push(rescue)
        }
    }

    const ownerCounts = new Map()
    const participantCounts = new Map()

    for (const rescue of rescues) {
        if (rescue.username) {
            ownerCounts.set(rescue.username, (ownerCounts.get(rescue.username) ?? 0) + 1)
        }
        if (rescue.rescuers) {
            for (const r of rescue.rescuers) {
                if (r.username) {
                    participantCounts.set(r.username, (participantCounts.get(r.username) ?? 0) + 1)
                }
            }
        }
    }

    return {rescues, isLoading, isFetching, refetch, ownerCounts, participantCounts, start, end}
}
